/**
 * The waitlist on Bookings (ReceptionistBee): who is waiting for a time per location, the offers
 * out, and the last 30 days. When an appointment is cancelled the freed time is texted to the
 * first matching customer, who replies YES to take it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListPlus, X } from 'lucide-react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Drawer, DrawerContent, EmptyState, Input, Label, LoadingState, Select, useToast } from '@buzzbee/ui';
import { useState } from 'react';
import { del, errorMessage, formatWhen, get, post, put } from '../lib/api';
import { useWorkspace } from '../lib/session';

const STATUS: Record<string, { label: string; variant: 'success' | 'warning' | 'default' | 'info' }> = {
  waiting: { label: 'Waiting', variant: 'info' },
  offered: { label: 'Offered', variant: 'warning' },
  booked: { label: 'Booked', variant: 'success' },
  expired: { label: 'Expired', variant: 'default' },
  cancelled: { label: 'Removed', variant: 'default' },
};

const EMPTY = { customerName: '', customerPhone: '', service: '', locationId: '', staffName: '', date: '', timeFrom: '', timeTo: '', smsOptIn: true };

function wanted(e: { date: string | null; timeFrom: string | null; timeTo: string | null }): string {
  const day = e.date ? new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(`${e.date}T12:00:00Z`)) : 'Any day';
  const time = e.timeFrom && e.timeTo ? `${e.timeFrom}-${e.timeTo}` : e.timeFrom ? `from ${e.timeFrom}` : e.timeTo ? `until ${e.timeTo}` : 'any time';
  return `${day}, ${time}`;
}

export function WaitlistCard() {
  const ws = useWorkspace();
  const qc = useQueryClient();
  const { success, error } = useToast();
  const q = useQuery({ queryKey: ['waitlist'], queryFn: () => get('/api/waitlist') });
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => get('/api/settings') });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const remove = useMutation({
    mutationFn: (id: string) => del(`/api/waitlist/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['waitlist'] }),
    onError: (e) => error('Could not remove', errorMessage(e)),
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => put('/api/waitlist/settings', { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['waitlist'] }),
    onError: (e) => error('Could not update', errorMessage(e)),
  });
  const add = useMutation({
    mutationFn: () =>
      post('/api/waitlist', {
        customerName: form.customerName,
        customerPhone: form.customerPhone,
        service: form.service,
        smsOptIn: form.smsOptIn,
        ...Object.fromEntries((['locationId', 'staffName', 'date', 'timeFrom', 'timeTo'] as const).filter((k) => form[k]).map((k) => [k, form[k]])),
      }),
    onSuccess: () => {
      success('Added to the waitlist');
      setOpen(false);
      setForm(EMPTY);
      void qc.invalidateQueries({ queryKey: ['waitlist'] });
    },
    onError: (e) => error('Could not add', errorMessage(e)),
  });
  const locations: { id: string; name: string; timezone: string }[] = q.data?.locations ?? [];
  const entries: any[] = q.data?.entries ?? [];
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <div>
          <CardTitle>Waitlist</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            {q.data && !q.data.enabled ? 'Off: cancelled times are not offered to anyone.' : `When a booking is cancelled, the first matching customer gets a text and has ${q.data?.offerMinutes ?? 30} minutes to reply YES.`}
          </p>
        </div>
        <span className="flex gap-2">
          {q.data ? (
            <Button variant="ghost" onClick={() => toggle.mutate(!q.data.enabled)} loading={toggle.isPending}>
              {q.data.enabled ? 'Turn off' : 'Turn on'}
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => setOpen(true)}>
            <ListPlus /> Add to waitlist
          </Button>
        </span>
      </CardHeader>
      <CardContent className="space-y-4 p-0">
        {q.isLoading ? <LoadingState /> : null}
        {q.data && entries.length === 0 ? <EmptyState title="No one is waiting" /> : null}
        {(locations.length > 1 ? locations : [{ id: '', name: '', timezone: ws.timezone }]).map((loc) => {
          const here = entries.filter((e) => !loc.id || e.locationId === loc.id);
          if (!here.length) return null;
          return (
            <div key={loc.id || 'all'}>
              {loc.name ? <p className="px-5 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{loc.name}</p> : null}
              <div className="divide-y">
                {here.map((e) => (
                  <div key={e.id} className={`flex flex-wrap items-center gap-3 px-5 py-3 ${e.status === 'waiting' || e.status === 'offered' ? '' : 'opacity-60'}`}>
                    <span className="min-w-0 flex-1 truncate text-sm">
                      <strong>{e.contactName ?? 'Customer'}</strong> <span className="figures text-muted-foreground">{e.contactPhone}</span>
                    </span>
                    <span className="text-sm">
                      {e.serviceName}
                      {e.staffName ? ` with ${e.staffName}` : ''}
                    </span>
                    <span className="figures w-44 text-sm text-muted-foreground">{wanted(e)}</span>
                    <Badge variant={STATUS[e.status]?.variant ?? 'default'}>{STATUS[e.status]?.label ?? e.status}</Badge>
                    {e.status === 'offered' && e.offeredStartsAt ? <Badge variant="outline">Offered {formatWhen(e.offeredStartsAt, loc.timezone)}</Badge> : null}
                    {e.status === 'waiting' || e.status === 'offered' ? (
                      <Button size="icon" variant="ghost" aria-label="Remove from waitlist" onClick={() => remove.mutate(e.id)} loading={remove.isPending && remove.variables === e.id}>
                        <X />
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </CardContent>
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent side="right" title="Add to waitlist">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="wl-name">Customer name</Label>
              <Input id="wl-name" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wl-phone">Mobile number</Label>
              <Input id="wl-phone" value={form.customerPhone} onChange={(e) => setForm({ ...form, customerPhone: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wl-service">Service</Label>
              <Select id="wl-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="Choose a service" options={(settings.data?.services ?? []).map((s: any) => ({ value: s.id, label: s.name }))} />
            </div>
            {locations.length > 1 ? (
              <div className="space-y-1.5">
                <Label htmlFor="wl-location">Location</Label>
                <Select id="wl-location" value={form.locationId} onChange={(e) => setForm({ ...form, locationId: e.target.value })} placeholder="Choose a location" options={locations.map((l) => ({ value: l.id, label: l.name }))} />
              </div>
            ) : null}
            <div className="space-y-1.5">
              <Label htmlFor="wl-staff">Team member (optional)</Label>
              <Input id="wl-staff" value={form.staffName} onChange={(e) => setForm({ ...form, staffName: e.target.value })} placeholder="Anyone" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wl-date">Day (optional)</Label>
              <Input id="wl-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="wl-from">From</Label>
                <Input id="wl-from" type="time" value={form.timeFrom} onChange={(e) => setForm({ ...form, timeFrom: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wl-to">Until</Label>
                <Input id="wl-to" type="time" value={form.timeTo} onChange={(e) => setForm({ ...form, timeTo: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.smsOptIn} onChange={(e) => setForm({ ...form, smsOptIn: e.target.checked })} /> They agreed to get a text when a spot opens
            </label>
            <Button type="submit" loading={add.isPending} className="w-full">
              Add
            </Button>
          </form>
        </DrawerContent>
      </Drawer>
    </Card>
  );
}
